import type { BaseDataItem, BreakpointRange, ScrubTriggerVariant } from '@wix/thunderbolt-becky-types'
import type { VsmContext, PageConfigEntry, PageConfigFn } from '@wix/thunderbolt-vsm-api-types'
import { removeHash } from '@wix/thunderbolt-vsm-api'
import type { CanvasPageConfig, CanvasVariation, CustomAnimation } from '../types'
import { name } from '../symbols'

interface EffectItem {
	type?: string
	id?: string
	value?: { values?: unknown[] }
}

interface VariantRelationItem extends BaseDataItem {
	to?: unknown
	variants?: Array<BreakpointRange | ScrubTriggerVariant>
}

interface AnimationItem extends Record<string, unknown> {
	type?: string
	namedEffect?: unknown
	customEffect?: unknown
}

function isEffectItem(e: unknown): e is EffectItem {
	return typeof e === 'object' && e !== null
}

function isAnimationItem(val: unknown): val is AnimationItem {
	return typeof val === 'object' && val !== null
}

function getUniqueObjects<T extends Record<string, unknown>>(items: T[]): T[] {
	const seen = new Set<string>()
	return items.filter((item) => {
		const key = JSON.stringify(item)
		if (seen.has(key)) return false
		seen.add(key)
		return true
	})
}

function sortByBreakpointOrder(
	sortDataByBreakpointsOrder: (data: Record<string, unknown>[], breakpointField: string) => Record<string, unknown>[],
	animationOptionsArr: Array<Record<string, unknown>>
): Array<Record<string, unknown>> {
	const sorted = sortDataByBreakpointsOrder(animationOptionsArr, 'breakpoint')
	return sorted.map((opt) => {
		const { trigger, triggerAndComp, breakpoint, ...rest } = opt
		return rest
	})
}

const canvasPageConfig: PageConfigFn<CanvasPageConfig> = (ctx: VsmContext, pageId: string) => {
	const { api, funcLib } = ctx
	const { components, breakpoints, env } = api

	if (!env.isResponsive()) {
		return { shouldLoadPageFeature: false }
	}

	const effectsListsForPage = components.getCompEffectsListsForPage(pageId)
	const compIdToCanvasEffectsMap: Record<
		string,
		Record<string, Array<{ animation: unknown; variants: Array<BreakpointRange | ScrubTriggerVariant> }>>
	> = {}

	for (const [compId, effectsList] of Object.entries(effectsListsForPage)) {
		const canvasEffects = effectsList.filter(isEffectItem).filter((e) => e.type === 'CanvasAnimation')
		if (canvasEffects.length === 0) continue

		const effectsMap: Record<
			string,
			Array<{ animation: unknown; variants: Array<BreakpointRange | ScrubTriggerVariant> }>
		> = {}
		for (const effect of canvasEffects) {
			const effectId = effect.id
			if (!effectId) continue
			const rawValues = effect.value?.values ?? []
			const variations = rawValues.map((item: unknown) => {
				const it = item as VariantRelationItem
				if (breakpoints.isVariantRelation(it)) {
					const variants = (it.variants ?? []).filter(Boolean)
					return { animation: it.to, variants }
				}
				return { animation: item, variants: [] as Array<BreakpointRange | ScrubTriggerVariant> }
			})
			effectsMap[effectId] = variations
		}
		compIdToCanvasEffectsMap[compId] = effectsMap
	}

	if (Object.keys(compIdToCanvasEffectsMap).length === 0) {
		return { shouldLoadPageFeature: false }
	}

	// Build canvasAnimationDataByCompId with targetId and variants on each item
	const canvasAnimationDataByCompId: Record<
		string,
		Record<string, Array<Record<string, unknown> & { targetId: string; variants: unknown[] }>>
	> = {}
	for (const [compId, effectsMap] of Object.entries(compIdToCanvasEffectsMap)) {
		canvasAnimationDataByCompId[compId] = {}
		for (const [effectId, effectVariations] of Object.entries(effectsMap)) {
			canvasAnimationDataByCompId[compId][effectId] = effectVariations.map((ev) => {
				if (!isAnimationItem(ev.animation)) {
					return { type: '', targetId: compId, variants: ev.variants }
				}
				const animation = ev.animation
				const hasActiveEffect =
					(animation.namedEffect !== undefined && animation.namedEffect !== null) ||
					(animation.customEffect !== undefined && animation.customEffect !== null)
				const type = animation.type ?? ''
				if (hasActiveEffect) {
					return { ...animation, targetId: compId, variants: ev.variants }
				}
				return { type, targetId: compId, variants: ev.variants }
			})
		}
	}

	// Add breakpoint, trigger, triggerAndComp, variants to each item
	const animationDataWithVariants: Record<
		string,
		Record<string, Array<Record<string, unknown> & { breakpoint?: string; variants?: unknown[] }>>
	> = {}
	for (const [compId, effectsMap] of Object.entries(canvasAnimationDataByCompId)) {
		animationDataWithVariants[compId] = {}
		for (const [effectId, arr] of Object.entries(effectsMap)) {
			animationDataWithVariants[compId][effectId] = arr.map((item) => {
				const variants = (item.variants as Array<BaseDataItem & { id?: string }>) ?? []
				const bpVariant = variants.find((v) => v?.type === 'BreakpointRange')
				const breakpoint = bpVariant?.id ?? ''
				const variantsArr = bpVariant ? [bpVariant] : []
				const triggerVariant = variants.find((v) => v?.type === 'Trigger') as ScrubTriggerVariant | undefined
				const trigger = triggerVariant ?? ({ trigger: '', componentId: '' } as unknown as ScrubTriggerVariant)
				const triggerAndComp =
					trigger?.componentId && trigger?.trigger ? `${trigger.componentId}-${trigger.trigger}` : ''
				if (triggerVariant) {
					return { ...item, breakpoint, trigger, triggerAndComp, variants: variantsArr }
				}
				return { ...item, breakpoint, variants: variantsArr }
			})
		}
	}

	// Build animationDataByCompId with canvasVariations and customAnimations sorted by breakpoint
	const sortDataByBp = breakpoints.sortDataByBreakpointsOrder.bind(breakpoints)

	const animationDataByCompId: CanvasPageConfig['animationDataByCompId'] = {}
	const allAnimationOptions: Record<string, Record<string, unknown>> = {}
	const canvasOptionsBpRangesAccum: Array<{ triggerBpId: string }> = []

	for (const [compId, effectsMap] of Object.entries(animationDataWithVariants)) {
		animationDataByCompId[compId] = {}
		for (const [effectId, animationOptionsArr] of Object.entries(effectsMap)) {
			const canvasOpts = animationOptionsArr.filter((o) => (o.type as string) === 'CanvasAnimationOptions') as Array<
				CanvasVariation & Record<string, unknown>
			>
			const customOpts = animationOptionsArr.filter((o) => (o.type as string) !== 'CanvasAnimationOptions') as Array<
				Record<string, unknown>
			>

			const canvasVariations = sortByBreakpointOrder(sortDataByBp, canvasOpts) as CanvasVariation[]

			for (const opt of canvasOpts) {
				canvasOptionsBpRangesAccum.push({
					triggerBpId: (opt.breakpoint as string) ?? '',
				})
			}

			const byTriggerAndComp: Record<string, Array<Record<string, unknown>>> = {}
			for (const opt of customOpts) {
				const key = (opt.triggerAndComp as string) ?? opt.id ?? ''
				if (!byTriggerAndComp[key]) byTriggerAndComp[key] = []
				byTriggerAndComp[key].push(opt)
			}
			const customAnimations: { [animationId: string]: CustomAnimation } = {}
			for (const arr of Object.values(byTriggerAndComp)) {
				const sorted = sortByBreakpointOrder(sortDataByBp, arr)
				const first = sorted[0] as Record<string, unknown> & { id?: string }
				const id = first?.id as string
				// No guard on id — when id is undefined it coerces to the string "undefined", matching Carmi's .keyBy() behavior
				customAnimations[id] = sorted as CustomAnimation
			}

			animationDataByCompId[compId][effectId] = {
				canvasVariations,
				customAnimations,
			}

			for (const opt of animationOptionsArr) {
				const id = opt.id as string
				// No guard on id — same reason as customAnimations above
				allAnimationOptions[id] = opt
			}
		}
	}

	// scrubTriggers: from customAnimations that are ScrubAnimationOptions (keyed by animationId, value = trigger only)
	const scrubTriggers: CanvasPageConfig['scrubTriggers'] = {}
	for (const effectsMap of Object.values(animationDataByCompId)) {
		for (const canvasEffectData of Object.values(effectsMap)) {
			for (const [animId, customArr] of Object.entries(canvasEffectData.customAnimations)) {
				const first = customArr[0]
				if (first && isAnimationItem(first) && first.type === 'ScrubAnimationOptions') {
					const optionData = allAnimationOptions[animId]
					if (optionData?.trigger) scrubTriggers[animId] = optionData.trigger as ScrubTriggerVariant
				}
			}
		}
	}

	// canvasBpRanges
	const breakpointsOrder = breakpoints.getBreakpointsOrder()
	const uniqueBpRanges = getUniqueObjects(canvasOptionsBpRangesAccum)
	const breakpointsRanges = components.getVariantsItemsByType('BreakpointRange') as Record<string, BreakpointRange>
	const canvasBpRanges =
		((funcLib?.resolveBpRanges?.(uniqueBpRanges, {
			breakpointsRanges,
			breakpointsOrder,
		}) ?? uniqueBpRanges.map(({ triggerBpId }) => breakpointsRanges[removeHash(triggerBpId)] ?? {}).filter(Boolean)) as
			| Array<BreakpointRange>
			| undefined) ?? []

	return {
		shouldLoadPageFeature: true,
		pageConfig: {
			animationDataByCompId,
			scrubTriggers,
			canvasBpRanges,
		},
	}
}

export const pageConfig: PageConfigEntry<CanvasPageConfig> = {
	name,
	configFactory: canvasPageConfig,
	experiment: 'specs.thunderbolt.vsmPageConfigs',
}
